import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", activation_word: "", turnover_confirmed: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.turnover_confirmed) {
      setErr("Please confirm your annual turnover is under £750,000");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, phone: form.phone || null });
      toast.success("Account created — now build your listing");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-xl mx-auto px-4 sm:px-6 py-12" data-testid="register-page">
      <div className="text-center mb-10">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <BrandLogo size="lg" />
          <span className="font-display text-2xl font-bold">TradeFlow UK</span>
        </Link>
        <h1 className="font-display text-4xl font-bold tracking-tight">List your business</h1>
        <p className="text-zinc-600 mt-2">Create an account with your activation word, add your listing and go live straight away.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-6 md:p-8 space-y-4" data-testid="register-form">
        <Field label="Your full name" id="name" value={form.name} onChange={(v) => update("name", v)} required testid="reg-name" />
        <Field label="Email" id="email" type="email" value={form.email} onChange={(v) => update("email", v)} required testid="reg-email" />
        <Field label="Password (min 8 characters)" id="password" type="password" value={form.password} onChange={(v) => update("password", v)} required testid="reg-password" />
        <Field label="Phone (optional)" id="phone" value={form.phone} onChange={(v) => update("phone", v)} testid="reg-phone" />
        <Field label="Activation word" id="activation" value={form.activation_word} onChange={(v) => update("activation_word", v)} required testid="reg-activation-word" />
        <label htmlFor="turnover" className="flex items-start gap-3 p-3 rounded-md border border-zinc-200 bg-zinc-50 cursor-pointer">
          <Checkbox id="turnover" checked={form.turnover_confirmed} onCheckedChange={(v) => update("turnover_confirmed", v === true)}
            className="mt-0.5" data-testid="reg-turnover-confirm" />
          <span className="text-sm text-zinc-700">I confirm my business has an annual turnover of under <strong>£750,000</strong>.</span>
        </label>

        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}

        <Button type="submit" disabled={loading} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-black rounded-md py-6 font-semibold" data-testid="register-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create business account"}
        </Button>
        <p className="text-center text-sm text-zinc-600">
          Already have one? <Link to="/login" className="font-medium text-black underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, id, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}
